import * as THREE from "three";
import type { AvatarConfig } from "@shared/types";
import type { Path } from "./Path";

/**
 * Lightweight opponent renderer for multiplayer. Cheaper than a full Player
 * (no animation rig, no physics) — just a chunky avatar block that tracks
 * (s, laneOffset) updates from the network.
 *
 * Each ghost gets a constant lateral slot offset so the whole field appears
 * side-by-side along the same path spine (Fall Guys vibe).
 */
export class Ghost {
  group = new THREE.Group();
  /** Latest authoritative position from the network. */
  targetS = 0;
  targetLaneOffset = 0;
  /** Smoothed values used for rendering. */
  s = 0;
  laneOffset = 0;
  /** Constant per-slot perpendicular offset — keeps players visually distinct. */
  readonly slotOffset: number;

  private nameSprite?: THREE.Sprite;
  private path: Path;

  constructor(
    public id: string,
    displayName: string,
    avatar: AvatarConfig,
    slotIdx: number,
    path: Path,
  ) {
    this.path = path;
    this.group.scale.setScalar(0.5);
    // 0.9m gap between players. Slot 0 → +1.35, slot 1 → -1.35, slot 2 → +2.7…
    const sign = slotIdx % 2 === 0 ? 1 : -1;
    this.slotOffset = sign * (Math.floor(slotIdx / 2) + 1) * 1.35;

    const skin = new THREE.Color(avatar.skinColor ?? "#d7a47f");
    const shirt = new THREE.Color(avatar.shirtColor ?? "#22c55e");
    const pants = new THREE.Color(avatar.pantsColor ?? "#334155");
    const mat = (c: THREE.Color, opacity = 0.85) =>
      new THREE.MeshStandardMaterial({ color: c, transparent: true, opacity });

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.7), mat(skin));
    head.position.y = 2.4;
    head.castShadow = true;
    this.group.add(head);

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.0, 0.5), mat(shirt));
    torso.position.y = 1.5;
    torso.castShadow = true;
    this.group.add(torso);

    for (const dx of [-0.62, 0.62]) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.95, 0.3), mat(skin));
      arm.position.set(dx, 1.5, 0);
      arm.castShadow = true;
      this.group.add(arm);
    }

    for (const dx of [-0.22, 0.22]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.35, 1.0, 0.35), mat(pants));
      leg.position.set(dx, 0.5, 0);
      leg.castShadow = true;
      this.group.add(leg);
    }

    // Floating name tag (canvas → texture → sprite).
    this.nameSprite = makeNameSprite(displayName);
    this.nameSprite.position.set(0, 3.4, 0);
    this.group.add(this.nameSprite);
  }

  setTarget(s: number, laneOffset: number) {
    this.targetS = s;
    this.targetLaneOffset = laneOffset;
  }

  update(dt: number) {
    // Smooth lerp toward authoritative target — hides the 12 Hz jitter.
    this.s += (this.targetS - this.s) * Math.min(1, dt * 12);
    this.laneOffset += (this.targetLaneOffset - this.laneOffset) * Math.min(1, dt * 12);
    const pos = this.path.positionAt(this.s, this.laneOffset + this.slotOffset);
    this.group.position.set(pos.x, 0, pos.z);
    this.group.rotation.y = this.path.yawAt(this.s);
  }

  dispose() {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        const m = o.material;
        if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
        else m.dispose();
      } else if (o instanceof THREE.Sprite) {
        o.material.map?.dispose();
        o.material.dispose();
      }
    });
  }
}

function makeNameSprite(text: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, 0, 256, 64);
  ctx.fillStyle = "#ffd166";
  ctx.font = "bold 32px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text.slice(0, 16), 128, 32);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(2.4, 0.6, 1);
  return sprite;
}
